// The Validate HTTP wire contract (Slice 5; slice-plan.md:70-78; D77/D32).
//
// The SINGLE source of truth for the Validate routes that apps/api (producer) and apps/web (consumer)
// build against - pinned here so the two sides cannot drift (the Slice-2 wire-drift lesson). Success
// payloads are the domain schemas from ../model/validate.js; this file adds the path params, request
// bodies, and the route path strings the api lane consumes verbatim. Every failure returns the shared
// `ErrorEnvelope { code, message }` (http/knowledge.ts:21) - one shape both sides narrow; a `not_found`
// and a `forbidden` on an app that is not the caller's return a byte-identical not-found (app-security
// §2 A01 - existence is not revealed). Guided workflow validation is a BUILD-phase gate - its route +
// DTOs live in ./build.ts (D5 reconciliation).
import { z } from "zod";
import { AppId } from "../ids.js";
import { NonEmptyString } from "../model/common.js";
import { EvidencePointer } from "../model/certify.js";
import { ApprovalGate, BrandDirection, MockupSet, ValidateUploadRequest, ValidateUploadResult, VerdictPack, } from "../model/validate.js";
/**
 * The canonical route path strings, for the api lane to register verbatim (D2 reconciliation). All
 * app-scoped, following the existing `/v1/apps/:appId/...` idiom (http/knowledge.ts; http/certify.ts).
 * `POST verdict` generates, `GET verdict` reads the latest, `GET verdict/:verdictId` reads one. Uploads
 * is PLURAL (a collection). Brand is a two-gate flow: pick + approve a DIRECTION, then generate +
 * approve MOCKUPS.
 */
export const VALIDATE_ROUTES = {
    verdict: "/v1/apps/:appId/validate/verdict",
    verdictById: "/v1/apps/:appId/validate/verdict/:verdictId",
    evidence: "/v1/apps/:appId/validate/evidence/:claimId",
    uploads: "/v1/apps/:appId/validate/uploads",
    brandDirections: "/v1/apps/:appId/brand/directions",
    brandDirectionsApprove: "/v1/apps/:appId/brand/directions/approve",
    mockups: "/v1/apps/:appId/brand/mockups",
    mockupsApprove: "/v1/apps/:appId/brand/mockups/approve",
};
/** Path params for the app-scoped Validate routes: the one app the request is scoped to. */
export const ValidateRouteParams = z.object({ appId: AppId });
/** Path params for the verdict-by-id read: the app + the verdict id. */
export const VerdictByIdParams = z.object({ appId: AppId, verdictId: NonEmptyString });
/** Path params for the on-demand evidence read: the app + the claim whose evidence is fetched. */
export const EvidenceParams = z.object({ appId: AppId, claimId: NonEmptyString });
// --- Verdict: POST (generate) / GET latest / GET :verdictId /v1/apps/:appId/validate/verdict ---
/** 200 -> the `VerdictPack` (cited-or-abstain by construction, §0 #4), else an `ErrorEnvelope`. */
export const VerdictResponse = VerdictPack;
// --- Evidence: GET /v1/apps/:appId/validate/evidence/:claimId (evidence shown on demand) ---
/** 200 -> the grounding pointers for a claim (slice-plan.md:75), else an `ErrorEnvelope`. */
export const EvidenceResponse = z.object({ evidence: z.array(EvidencePointer) });
// --- Uploads: POST /v1/apps/:appId/validate/uploads (the hardened gate, D77) ---
/** The declared upload metadata (bytes arrive multipart at the edge; scan/store happen server-side). */
export const ValidateUploadRequestBody = ValidateUploadRequest;
/** 200 -> the `ValidateUploadResult` (accepted-with-refs | rejected-with-no-refs), else an `ErrorEnvelope`. */
export const ValidateUploadResponse = ValidateUploadResult;
// --- Brand directions: POST generate + POST approve /v1/apps/:appId/brand/directions[...] (D32) ---
/** 200 -> the brand directions the founder picks from (D32), else an `ErrorEnvelope`. */
export const BrandDirectionsResponse = z.object({ directions: z.array(BrandDirection) });
/** POST body: which direction the founder approves. */
export const BrandDirectionsApproveRequest = z.object({ directionId: NonEmptyString });
/** 200 -> the resulting `ApprovalGate` (an `approved` gate records who + when), else an `ErrorEnvelope`. */
export const BrandDirectionsApproveResponse = ApprovalGate;
// --- Mockups: POST generate + POST approve /v1/apps/:appId/brand/mockups[...] (D32) ---
/** POST body: which approved direction to generate mockups for. */
export const MockupsRequest = z.object({ directionId: NonEmptyString });
/** 200 -> the generated `MockupSet` for a chosen direction (D32), else an `ErrorEnvelope`. */
export const MockupsResponse = MockupSet;
/** POST body: which direction's mockups the founder approves (Build proceeds only on approval, D32). */
export const MockupsApproveRequest = z.object({ directionId: NonEmptyString });
/** 200 -> the resulting `ApprovalGate` (gates Build), else an `ErrorEnvelope`. */
export const MockupsApproveResponse = ApprovalGate;
//# sourceMappingURL=validate.js.map